import React, {
  useState, useEffect, memo, useMemo,
} from 'react';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import { FastField, connect } from 'formik';

import {
  TableContainer, TableText, TableHeader, TableDateContainer, StyledRadio, StyledDateP, ErrorText,
} from './style';

import { parse } from './parser';

const CheckboxCell = ({ index }) => {
  const flightPosition = index.toString();
  return (
    <FastField name="flightSelected">
      {({ field, form }) => (
        <StyledRadio
          {...field}
          value={flightPosition}
          checked={flightPosition === field.value}
          onChange={() => form.setFieldValue(field.name, flightPosition)}
        />
      )}
    </FastField>
  );
};

CheckboxCell.propTypes = {
  index: PropTypes.number.isRequired,
};

const renderHeaderTable = title => <TableHeader>{title}</TableHeader>;
const renderTextTable = cellData => <TableText>{cellData.value}</TableText>;
const renderDateTable = cellData => (
  <TableDateContainer>
    <StyledDateP>{cellData.value.hour}</StyledDateP>
    <StyledDateP>{cellData.value.date}</StyledDateP>
  </TableDateContainer>
);

const FlightTableData = memo(({ data }) => {
  if (!data.length) {
    return null;
  }
  return (
    <TableContainer>
      <ReactTable
        data={data}
        filterable={false}
        columns={[
          {
            Header    : '',
            accessor  : 'actions',
            sortable  : false,
            filterable: false,
            maxWidth  : 64,
            Cell      : CheckboxCell,
          },
          {
            Header  : renderHeaderTable('Airline'),
            accessor: 'airline',
            sortable: false,
            maxWidth: 80,
            Cell    : renderTextTable,
          },
          {
            Header  : renderHeaderTable('Operator'),
            accessor: 'operator',
            sortable: false,
            maxWidth: 80,
            Cell    : renderTextTable,
          },
          {
            Header  : renderHeaderTable('Flight'),
            accessor: 'flightNumber',
            sortable: false,
            maxWidth: 80,
            Cell    : renderTextTable,
          },
          {
            Header  : renderHeaderTable('Departure time'),
            accessor: 'departure',
            sortable: false,
            minWidth: 100,
            Cell    : renderDateTable,
          },
          {
            Header  : renderHeaderTable('Arrival time'),
            accessor: 'arrival',
            sortable: false,
            Cell    : renderDateTable,
          },
          {
            Header  : renderHeaderTable('Origin / Destination'),
            accessor: 'segments',
            sortable: false,
            minWidth: 120,
            Cell    : renderTextTable,
          },
        ]}
        minRows={0}
        showPaginationTop={false}
        showPaginationBottom={false}
        className="-striped -highlight"
      />
    </TableContainer>
  );
});

FlightTableData.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const FlightTable = ({ flightProducts, formik }) => {
  const [data, setData] = useState([]);
  const { setFieldValue } = formik;

  const parsedData = useMemo(() => parse(flightProducts.data), [flightProducts.data]);

  useEffect(() => {
    if (flightProducts.error) {
      setFieldValue('flightSelected', undefined);
      setData([]);
      return;
    }
    setData(parsedData);
    // if only we have one flight we want it checked by default
    if (flightProducts.data.length === 1) {
      setFieldValue('flightSelected', '0');
    } else {
      setFieldValue('flightSelected', undefined);
    }
  }, [flightProducts, setFieldValue, parsedData]);

  return flightProducts.error
    ? <ErrorText>{flightProducts.error}</ErrorText>
    : <FlightTableData data={data} />;
};

FlightTable.propTypes = {
  flightProducts: PropTypes.shape({
    data : PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.string,
  }).isRequired,
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func,
  }).isRequired,
};

export default connect(FlightTable);
